/**
 * Clipboard utilities for copying canvas images with memory-aware optimizations
 * 
 * This file is kept dependency-free to avoid test issues with ESM modules
 */

/**
 * Copies a canvas image and URL to the clipboard as HTML
 * 
 * Features:
 * - Memory-aware canvas scaling based on device memory
 * - Progressive quality reduction for large canvases
 * - Comprehensive error handling
 * 
 * @param canvas The canvas element to copy
 * @param linkUrl The URL to include with the image
 * @param type The type of content (e.g., 'hangar', 'ramp')
 * @returns boolean indicating success or failure
 */
export const copyImageAndUrlToClipboard = async (
  canvas: HTMLCanvasElement,
  linkUrl: string,
  type: string = "hangar"
): Promise<boolean> => {
  try {
    // Estimate available memory - use navigator.deviceMemory if available or default to conservative estimate
    const estimatedDeviceMemory = (navigator as any).deviceMemory || 4; // in GB, defaults to 4GB if not available
    console.log(`Device memory detected: ${estimatedDeviceMemory}GB`);

    // Scale max canvas dimensions based on available memory
    // Less aggressive limits to prevent content cropping
    const MAX_CANVAS_DIMENSION = Math.min(
      4096, // absolute maximum
      estimatedDeviceMemory >= 16 ? 4096 :
      estimatedDeviceMemory >= 8 ? 3500 : // Increased from 3072
      estimatedDeviceMemory >= 4 ? 2500 : 2000 // Increased from 2048 and 1536
    );
    
    // Maximum canvas area (width * height) based on memory
    // Increased safety margin to prevent content loss
    const MAX_CANVAS_AREA = MAX_CANVAS_DIMENSION * MAX_CANVAS_DIMENSION * 0.85; // Increased from 0.75 to 0.85
    
    // Check if the canvas exceeds maximum dimensions
    let scaledCanvas = canvas;
    const width = canvas.width;
    const height = canvas.height;
    const area = width * height;

    // If canvas is too large by area or dimensions, create a scaled down version
    if (area > MAX_CANVAS_AREA || width > MAX_CANVAS_DIMENSION || height > MAX_CANVAS_DIMENSION) {
      console.log(`Canvas needs scaling - dimensions: ${width}x${height}, area: ${area}, MAX_DIMENSION: ${MAX_CANVAS_DIMENSION}, MAX_AREA: ${MAX_CANVAS_AREA}`);

      // Calculate scale factor to maintain aspect ratio
      let scaleFactor = 1;

      // First check area constraint with buffer for content preservation
      if (area > MAX_CANVAS_AREA) {
        scaleFactor = Math.sqrt(MAX_CANVAS_AREA / area) * 0.95; // Add 5% buffer to prevent edge content loss
      }
      
      // Then check dimension constraints with buffer
      scaleFactor = Math.min(
        scaleFactor,
        (MAX_CANVAS_DIMENSION / width) * 0.95, // Add 5% buffer
        (MAX_CANVAS_DIMENSION / height) * 0.95 // Add 5% buffer
      );
      
      // Ensure minimum scale factor to prevent excessive downsizing
      scaleFactor = Math.max(scaleFactor, 0.3); // Don't scale below 30% of original

      // Create a new canvas with scaled dimensions
      const scaledWidth = Math.floor(width * scaleFactor);
      const scaledHeight = Math.floor(height * scaleFactor);
      console.log(`Scaling canvas to: ${scaledWidth}x${scaledHeight} (scale factor: ${scaleFactor.toFixed(2)})`);

      scaledCanvas = document.createElement('canvas');
      scaledCanvas.width = scaledWidth;
      scaledCanvas.height = scaledHeight;
      
      // Draw the original canvas onto the scaled canvas
      const ctx = scaledCanvas.getContext('2d');

      // Use appropriate quality based on scale factor
      if (scaleFactor < 0.5) {
        ctx.imageSmoothingQuality = 'high';
      } else if (area > MAX_CANVAS_AREA * 0.5) {
        ctx.imageSmoothingQuality = 'medium';
      } else {
        ctx.imageSmoothingQuality = 'high';
      }

      ctx.drawImage(canvas, 0, 0, width, height, 0, 0, scaledWidth, scaledHeight);
    }
    
    // Try to get dataURL from canvas with error handling and progressive quality reduction
    const getCanvasDataUrl = (canvas: HTMLCanvasElement, quality = 1.0): string | null => {
      try {
        // Try with the specified quality
        const dataUrl = canvas.toDataURL('image/png', quality);
        
        // Validate that the dataURL is not empty or invalid
        if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 100) {
          console.error(`Generated dataURL is invalid or empty with quality ${quality}`);

          // If quality is already at minimum, return null
          if (quality <= 0.5) {
            return null;
          }

          // Try again with lower quality
          console.log(`Error occurred, retrying with lower quality (${(quality - 0.2).toFixed(1)})`);
          return getCanvasDataUrl(canvas, quality - 0.2);
        }
        
        return dataUrl;
      } catch (err) {
        console.error('Error generating canvas dataURL:', err);

        // If error occurs and quality is still high, try with lower quality
        if (quality > 0.5) {
          console.log(`Error occurred, retrying with lower quality (${(quality - 0.2).toFixed(1)})`);
          return getCanvasDataUrl(canvas, quality - 0.2);
        }

        return null;
      }
    };
    
    // Get the dataURL with progressive quality reduction
    const dataUrl = getCanvasDataUrl(scaledCanvas);
    if (!dataUrl) {
      console.error('Failed to generate canvas dataURL after multiple attempts');
      return false;
    }
    
    // Create HTML content for clipboard
    const html = `<div>
      <img src="${dataUrl}" />
      <p>Edit ${type} here: <a href="${linkUrl}">${linkUrl}</a></p>
    </div>`;

    // Create blob and clipboard item
    const leBlob = new Blob([html], { type: "text/html" });
    const item = new ClipboardItem({ "text/html": leBlob });
    
    // Write to clipboard with proper async/await handling
    try {
      await navigator.clipboard.write([item]);
      return true;
    } catch (err) {
      console.error('Error writing to clipboard:', err);
      throw err; // Re-throw to be caught by outer try-catch
    }
  } catch (err) {
    const errorDetails = {
      message: err.message || "Unknown error",
      stack: err.stack,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      canvasArea: canvas.width * canvas.height,
      url: linkUrl,
      type: type,
      deviceMemory: (navigator as any).deviceMemory || "unknown",
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    
    console.error('Failed to copy to clipboard - detailed error:', errorDetails);
    return false;
  }
};
